import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, ElementRef, Input, OnDestroy, ViewChild
       } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';

import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { TemplateConfig } from 'collage-designer/collage-designer-config';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { StageService } from 'collage-designer/stage/stage.service';
import { TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';

class PreviewLoadingError extends TrivialError
{
  // i18n.error.previewLoadingError.title
  // i18n.error.previewLoadingError.message
  protected _name: string = 'PreviewLoadingError';
}

@Component({
  selector: 'preview',
  template: `
    <div class="wrapper">
      <div #spinner class="spinner">
        <mat-spinner color="accent"></mat-spinner>
      </div>
      <canvas #canvas id="{{canvasId}}"></canvas>
    </div>
  `,
  styleUrls: ['./preview.component.scss']
})
export class PreviewComponent implements OnDestroy
{
  public static readonly TRY_RESTART_TIMEOUT: number = 250; /* ms */
  public static PREVIEW_OPTIONS: any = {
      grabMoveEnabled: false,
      rotationEnabled: true,
      rotationStep: 0.8,
      autoUpdateEnabled: false,
  };
  private static MAX_SPINNER_TIME: number = 10000; /* ms */

  @Input() public stageID: string;

  @ViewChild('canvas') public canvas: ElementRef;
  @ViewChild('spinner') public spinner: ElementRef;

  public canvasId: string;

  private rendererFactory: any;
  private renderer: any;
  private designStepId: number;
  private spinnerActiveSince: number|undefined = undefined;

  private subscriptions: Subscription = new Subscription();
  private templateChangedSubscription:
      AsyncEventEmitterSubscriber<TemplateConfig>;

  constructor(
      private http: HttpClient,
      private errorService: ErrorService,
      private stageService: StageService,
      private toolboxService: ToolboxService,
      private designStepService: DesignStepService,
      private placeholderService: PlaceholderService,
      private imageStoreService: ImageStoreService,
  )
  {
    this.designStepId = this.designStepService.getDesignStepId();
    const randomHexString = Math.random().toString(16).substr(4, 4);
    this.canvasId = 'previewCanvas' + randomHexString;

    const scheduleUpdate = async () => {
      if (this.renderer &&
          this.designStepService.getDesignStepId() === this.designStepId) {
        this.renderer.scheduleUpdate();
      }
    };

    this.subscriptions.add(
        this.stageService.stageConfigApplied.subscribe(() => {
          if (this.renderer) {
            this.renderer.updateSVGReference(this.stageID);
          }
        }));
    this.subscriptions.add(
        this.toolboxService.placeholderChanged.subscribe(() => {
          if (this.stageService.getIsStageLoaded()) {
            scheduleUpdate();
          }
        }));
    this.subscriptions.add(
        this.placeholderService.placeholderChanged.subscribe(() => {
          if (this.stageService.getIsStageLoaded()) {
            scheduleUpdate();
          }
        }));
    this.templateChangedSubscription =
        this.designStepService.templateChanged.subscribe(async () => {
          if (this.shouldUseExternalRenderer) {
            this.showSpinner();
          }
        });

    if (!Utils.isSmallDevice()) {
      PreviewComponent.PREVIEW_OPTIONS.grabMoveEnabled = true;
    }
  }

  @Input()
  public set previewUrl(previewUrl: string|undefined)
  {
    if (!previewUrl) {
      return;
    }

    this.replacePreviewRenderer(previewUrl).then(() => {
      this.restartRenderer();
    });
  }

  public async ngOnDestroy(): Promise<void>
  {
    this.subscriptions.unsubscribe();
    this.designStepService.templateChanged.unsubscribe(
        this.templateChangedSubscription);

    await this.removeCurrentRenderer();
  }

  private async replacePreviewRenderer(previewUrl: string): Promise<void>
  {
    const code = await this.loadPreviewRendererCode(previewUrl);

    // tslint:disable-next-line no-eval
    this.rendererFactory = eval(code);
  }

  private async restartRenderer(): Promise<void>
  {
    this.showSpinner();
    while (!document.getElementById(this.canvasId) || !this.rendererFactory ||
           !this.stageID) {
      await Utils.sleep(PreviewComponent.TRY_RESTART_TIMEOUT);
      return await this.restartRenderer();
    }

    try {
      this.renderer = new this.rendererFactory(
          '#' + this.canvasId, this.stageID, this.previewOptions);
    }
    catch (exc) {
      console.warn('creation of preview renderer failed: ' + exc +
                   ', needs retry');
      await Utils.sleep(PreviewComponent.TRY_RESTART_TIMEOUT);
      return await this.restartRenderer();
    }

    this.subscriptions.add(
      this.renderer.loaded.subscribe(loaded => {
        if (loaded) {
          this.hideSpinner();
        }
      }));
  }

  private showSpinner(): void
  {
    this.spinnerActiveSince = Date.now();
    this.spinner.nativeElement.style.display = 'inherit';
    this.canvas.nativeElement.style.opacity = 0.5;
    setTimeout(() => {
      if (this.spinnerActiveSince &&
          this.spinnerActiveSince + PreviewComponent.MAX_SPINNER_TIME
          <= Date.now()) {
        this.hideSpinner();
      }
    }, PreviewComponent.MAX_SPINNER_TIME);
  }

  private hideSpinner(): void
  {
    this.spinnerActiveSince = undefined;
    this.spinner.nativeElement.style.display = 'none';
    this.canvas.nativeElement.style.opacity = 1;
  }

  private get previewOptions(): any
  {
    return Object.assign({
      svgRenderer: this.shouldUseExternalRenderer
          ? this.imageStoreService.renderSvg.bind(this.imageStoreService)
          : undefined,
    }, PreviewComponent.PREVIEW_OPTIONS);
  }

  private get shouldUseExternalRenderer(): boolean
  {
    return Utils.isIphone() || Utils.isSafari();
  }

  private async removeCurrentRenderer(): Promise<void>
  {
    if (!this.renderer) {
      return;
    }

    try {
      await this.renderer.finalize();
    } catch (exc) {
      console.warn('could not shut down the renderer properly');
    }

    this.renderer = undefined;
  }

  private async loadPreviewRendererCode(location: string): Promise<string>
  {
    const headers = new HttpHeaders({
      'Accept': 'application/javascript'
    });

    const resp = <any> await this.http.get(location,
        { responseType: 'text', headers: headers })
      .pipe(Utils.retryPipe())
      .toPromise()
      .catch((exc) => {
        const message = `[${exc.status} ${exc.statusText}] "${exc.url}"`.trim();
        const reference = [location];
        this.errorService.add(
          new PreviewLoadingError(message, reference), 'PreviewComponent');
        this.errorService.flush('PreviewComponent');
      });
    return resp;
  }
}



// WEBPACK FOOTER //
// ./src/app/controls/preview/preview.component.ts